import { useMutation } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { faFileExport } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { generateExports } from 'api/contacts/exports';
import Content from 'assets/js/components/Content';
import * as ExportDataDialog from 'assets/js/components/ExportDataDialog';
import * as ExportDialog from 'assets/js/components/ExportDialog';
import * as DropdownMenu from 'components/dropdown-menu';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import * as TicketActions from 'components/pages/tickets/TicketActions';
import { TicketsTable } from 'components/pages/tickets/TicketsTable';
import { TicketsTableProvider } from 'components/pages/tickets/TicketsTableProvider';
import { createTicketColumns } from 'components/pages/tickets/ticketColumns';
import * as SubNav from 'components/sub-nav';
import { TransactionsSubNavTabs } from 'components/sub-nav/TransactionsSubNavTabs';
import * as Table from 'components/table';
import { TableCoreResourceProvider } from 'components/table/TableCoreResourceContext';
import { TicketScanModal } from 'components/ticket-scan-modal';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { GivebutterAxiosError } from 'types/error';

const Tickets = () => {
    const { newExportModal } = useFlags();
    const { accountId } = useCurrentAccount();
    const { error } = useToast();
    const { filters } = useFiltersByKey('tickets');
    const { mutateAsync: exportTickets } = useMutation({
        mutationFn: () =>
            generateExports(accountId, {
                scope: 'all',
                object: 'account',
                report_type: 'csv',
                resource: 'tickets',
                object_id: accountId,
                filters,
            }),
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Unable to generate report');
        },
    });

    return (
        <div className={'flex flex-col gap-y-4'}>
            <SubNav.Root>
                <SubNav.Header>
                    <SubNav.Title>Tickets</SubNav.Title>
                    <SubNav.Actions>
                        {newExportModal ? (
                            <ExportDialog.Root reportType="tickets">
                                <ExportDialog.Trigger asChild={true}>
                                    <Button variant="outline" color="neutral">
                                        <FontAwesomeIcon icon={faFileExport} />
                                        Export
                                    </Button>
                                </ExportDialog.Trigger>
                            </ExportDialog.Root>
                        ) : (
                            <ExportDataDialog.Root label="Tickets" onSubmit={exportTickets}>
                                <ExportDataDialog.Trigger asChild>
                                    <Button variant="outline" color="neutral">
                                        <FontAwesomeIcon icon={faFileExport} />
                                        Export
                                    </Button>
                                </ExportDataDialog.Trigger>
                            </ExportDataDialog.Root>
                        )}
                        <TicketScanModal />
                    </SubNav.Actions>
                </SubNav.Header>
                <TransactionsSubNavTabs />
            </SubNav.Root>
            <TicketsTable />
        </div>
    );
};

const TicketsWrapper = () => (
    <TableCoreResourceProvider
        resource={'tickets'}
        initialState={{
            sortingState: [{ id: 'created_at', desc: true }],
            columnVisibilityState: {
                email: false,
                phone: false,
                price: false,
                campaign: false,
                discount: false,
            },
        }}
        columns={columnHelper => {
            const ticketColumns = createTicketColumns(columnHelper);

            return [
                ticketColumns.contactName(),
                ticketColumns.createdAt(),
                ticketColumns.type(),
                ticketColumns.number(),
                ticketColumns.checkedInAt(),
                ticketColumns.latestCheckin(),
                ticketColumns.email(),
                ticketColumns.phone(),
                ticketColumns.price(),
                ticketColumns.campaign(),
                ticketColumns.discount(),
                columnHelper.display({
                    id: Table.Constants.Actions,
                    cell: info => {
                        const ticket = info.row.original;

                        return (
                            <TicketActions.Root ticket={ticket}>
                                <DropdownMenu.Root>
                                    <Table.RowDropdownTrigger />
                                    <DropdownMenu.Content sideOffset={5} align={'end'}>
                                        <TicketActions.ViewTicket />
                                        <TicketActions.ViewTransaction />
                                        <TicketActions.Reassign />
                                        <TicketActions.Download />
                                        <TicketActions.Revoke />
                                        <TicketActions.Resend />
                                    </DropdownMenu.Content>
                                </DropdownMenu.Root>
                            </TicketActions.Root>
                        );
                    },
                    meta: {
                        ignoreLink: true,
                    },
                }),
            ];
        }}
        options={{
            enableHiding: true,
            enableSorting: true,
            enablePinning: true,
            enableExpanding: true,
            meta: {
                enableColumnOrder: true,
            },
        }}
    >
        <TicketsTableProvider>
            <Content>
                <Tickets />
            </Content>
        </TicketsTableProvider>
    </TableCoreResourceProvider>
);

export default TicketsWrapper;
